import { useInfiniteQuery } from '@tanstack/react-query'
import { CommentResponse, CommentsHttpClient } from '@omnea-app/comments/commentsHttpClient'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'

export const mentionsPageSize = 50

export const mentionsQueryKey = 'mentions'

export const getMentionsQueryKey = (isResolved: boolean, requestId?: string) => [
  mentionsQueryKey,
  isResolved ? 'resolved' : 'unresolved',
  ...(requestId ? [requestId] : []),
]

export type MentionsResponse = CommentResponse<'/mentions', 'get'>

export const useGetMentions = (options: { isResolved: boolean; requestId?: string }) => {
  const { getToken } = useAuthenticatedUser()
  const { isResolved, requestId } = options

  const query = useInfiniteQuery({
    queryKey: getMentionsQueryKey(isResolved, requestId),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const apiClient = new CommentsHttpClient({ authorization: await getToken() })
      const { data, error } = await apiClient.POST('/mentions', {
        body: { paginationModel: { page: pageParam, pageSize: mentionsPageSize }, isResolved, requestId },
      })
      throwIfApiError(error)
      return data
    },
    getNextPageParam: (lastPage, allPages) => {
      // Use the latest response's totalCount, not the first page's — the server
      // recomputes it each request, so a frozen first-page value could otherwise
      // loop forever fetching empty pages if the count drops between fetches.
      const loaded = allPages.reduce((total, page) => total + (page?.mentions?.length ?? 0), 0)
      const totalCount = lastPage?.totalCount ?? 0
      return loaded < totalCount ? allPages.length : undefined
    },
  })

  const mentions = query.data?.pages.flatMap((page) => page?.mentions ?? [])
  // Latest page's count — the server recomputes totalCount each request.
  const totalCount = query.data?.pages.at(-1)?.totalCount

  // Expose a flattened `data` so consumers keep reading `data.mentions`, plus the
  // infinite-query controls for load-more.
  return {
    ...query,
    data: query.data ? { mentions: mentions ?? [], totalCount: totalCount ?? 0 } : undefined,
  }
}
